import React from "react";
import { Box, Typography, Button, Tooltip, GlobalStyles } from "@mui/material";
import { styled } from "@mui/material/styles";
import { keyframes } from "@emotion/react";

// --- Font and Cursor ---
const globalStyles = (
  <GlobalStyles
    styles={{
      '@import url("https://fonts.googleapis.com/css2?family=Special+Elite&display=swap")':
        {},
      "body, html, #root": {
        height: "100%",
        width: "100%",
        margin: 0,
        padding: 0,
        overflow: "hidden",
        background: "#0d0c0f", // Darker background
        fontFamily: '"Special Elite", cursive',
        cursor:
          'url(\'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>\'), auto',
      },
    }}
  />
);

// --- Keyframe Animations ---
const keyframesRain = keyframes`
  0% {
    transform: translateY(-10vh) translateX(-20%);
    opacity: 1;
  }
  70% {
    opacity: 1;
  }
  100% {
    transform: translateY(70vh) translateX(20%);
    opacity: 0;
  }
`;

const keyframesSplash = keyframes`
  0% { transform: scale(0); opacity: 1; }
  80% { transform: scale(1); opacity: 1; }
  100% { transform: scale(1.2); opacity: 0; }
`;
const keyframesLampFlicker = keyframes`
  0%,100% { opacity: 0.5; }
  50%     { opacity: 1; }
`;

const keyframesSteam = keyframes`
  0% { transform: translateY(0) scaleX(1); opacity: 0.5; }
  100% { transform: translateY(-30px) scaleX(1.5); opacity: 0; }
`;

const keyframesSmoke = keyframes`
  0% { transform: translateY(0) scale(1); opacity: 0.3; }
  100% { transform: translateY(-40px) scale(1.5); opacity: 0; }
`;

const keyframesDust = keyframes`
  0%   { transform: translate(0, 0) scale(1);   opacity: 0;   }
  10%  { opacity: 0.25; }
  90%  { opacity: 0.35; }
  100% { transform: translate(-15px, -20px) scale(1.15); opacity: 0; }
`;

const keyframesNeon = keyframes`
  0%, 100% { opacity: 0.7; } 50% { opacity: 0.8; }
`;

const keyframesLightning = keyframes`
  0%, 98%, 100% { opacity: 0; }
  98.1% { opacity: 0.9; }
  98.2% { opacity: 0.1; }
  98.3% { opacity: 1; }
  98.5% { opacity: 0; }
`;

// --- UPDATED: PageContainer with Vignette ---
const PageContainer = styled(Box)({
  height: "100%",
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "relative",
  overflow: "hidden",
  backgroundColor: "#001219",
  zIndex: 0,
  // NEW: Adds a vignette effect for depth and atmosphere
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background:
      "radial-gradient(circle, transparent 50%, rgba(0,0,0,0.6) 100%)",
    zIndex: 15, // On top of everything
    pointerEvents: "none", // Allows clicking through it
  },
});

const WindowContainer = styled(Box)({
  position: "relative",
  width: "100%",
  height: "100%",
  overflow: "hidden",
  zIndex: 1,
});

const LightningFlash = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: "linear-gradient(to bottom, #fff, #ddd)",
  opacity: 0,
  animation: `${keyframesLightning} 10s ease-in-out infinite`,
  zIndex: 1, // CORRECTED: Moved to the back
}));

// --- UPDATED: WindowFrame with more depth and grimy glass ---
const WindowFrame = styled(Box)({
  position: "absolute",
  top: "37%",
  left: "50%",
  width: "50vw",
  height: "60vh",
  transform: "translate(-50%, -50%)",
  border: "15px solid #2f2927",
  borderRadius: "3px",
  // ENHANCED: Deeper shadow and an inset shadow to make the glass look recessed
  boxShadow: "0 0 30px rgba(0,0,0,0.8), inset 0 0 15px 5px rgba(0,0,0,0.7)",
  // ENHANCED: Layered gradients for grimy, streaked glass
  background: `
        linear-gradient(to bottom, rgba(180, 190, 200, 0.05) 0%, rgba(100, 110, 120, 0.1) 100%),
        linear-gradient(to right, rgba(255, 255, 255, 0.01) 0%, rgba(255, 255, 255, 0.05) 50%, rgba(255, 255, 255, 0.01) 100%)
    `,
  overflow: "hidden",
  zIndex: 3,
});

const WindowMullion = styled(Box)({
  position: "absolute",
  backgroundColor: "#2c1e1a",
  boxShadow: "0 0 5px rgba(0,0,0,0.8)",
  "&.vertical": {
    top: 0,
    left: "50%",
    width: "15px",
    height: "100%",
    transform: "translateX(-50%)",
  },
  "&.horizontal": {
    top: "50%",
    left: 0,
    width: "100%",
    height: "15px",
    transform: "translateY(-50%)",
  },
});

const NeonSignGlow = styled(Box)({
  position: "absolute",
  top: "40%",
  left: "20%",
  width: "200px",
  height: "100px",
  background:
    "radial-gradient(circle, rgba(255, 50, 50, 0.3) 0%, rgba(255, 50, 50, 0) 70%)",
  animation: `${keyframesNeon} 3s infinite alternate`,
  zIndex: 2, // Place behind window frame
});

const Raindrop = styled("div")({
  position: "absolute",
  width: "1px",
  height: "10px",
  background: "rgba(200, 210, 230, 0.4)", // Made slightly more opaque
  animation: `${keyframesRain} linear infinite`,
  transformOrigin: "bottom",
  filter: "blur(0.5px)",
  zIndex: 4, // CORRECTED: Placed in front of the window frame
});

const Desk = styled(Box)({
  position: "absolute",
  bottom: 0,
  left: 0,
  width: "100%",
  height: "25%",
  zIndex: 1,

  // ENHANCED: Layered gradients for a richer, more complex wood finish
  background: `
        /* 1. NEW: Polished surface sheen - a very subtle light reflection */
        radial-gradient(
            ellipse at 50% 0%, 
            rgba(255, 255, 255, 0.05) 0%, 
            transparent 40%
        ),
        /* 2. ENHANCED: More complex, layered wood grain for realism */
        repeating-linear-gradient(
            90deg,
            rgba(0,0,0,0.2), 
            rgba(0,0,0,0.2) 1px, 
            transparent 1px, 
            transparent 40px
        ),
        repeating-linear-gradient(
            90deg,
            rgba(255,255,255,0.02) 0, 
            rgba(255,255,255,0.02) 1px, 
            transparent 1px, 
            transparent 60px
        ),
        /* 3. ENHANCED: Base wood color with more tones (dark reds and browns) */
        linear-gradient(180deg, #4a3c3c, #4d322f 80%, #2a1f1d 100%)
    `,

  // ENHANCED: A more complex shadow to suggest the desk is solid and thick
  boxShadow: "inset 0 6px 4px -4px rgba(0,0,0,0.6)",

  // NEW: This pseudo-element creates a sharp highlight on the front edge
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "1px",
    background:
      "linear-gradient(to right, transparent, rgba(255,255,255,0.2) 40%, rgba(255,255,255,0.2) 60%, transparent)",
  },

  // NEW: This pseudo-element creates the thick front-facing part of the desk
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "10px",
    background: "linear-gradient(to bottom, #5c4a4a, #3a2d2d)",
    boxShadow: "0 2px 3px rgba(0,0,0,0.4)",
  },
});

const DeskLampLight = styled(Box)({
  position: "absolute",
  bottom: "22%", // lift a bit so it sits above desk
  left: "50%",
  width: "520px",
  height: "520px",
  background:
    "radial-gradient(circle at 50% 35%, \
       rgba(255,235,200,0.7) 0%, \
       rgba(255,225,170,0.5) 40%, \
       rgba(255,225,170,0.2) 65%, \
       rgba(255,225,170,0) 80%)",
  mixBlendMode: "screen", // key for visibility on dark bg
  filter: "blur(2px)",
  transform: "translateX(-50%)",
  animation: `${keyframesLampFlicker} 2.6s ease-in-out infinite`,
  pointerEvents: "none",
  zIndex: 14,
});

const DustParticle = styled(Box)({
  position: "absolute",
  width: "2px",
  height: "2px",
  background: "rgba(255, 255, 255, 0.9)",
  borderRadius: "50%",
  animation: `${keyframesDust} linear infinite`,
});

const DeskItem = styled(Box)({
  position: "absolute",
  cursor:
    'url(\'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>\'), auto',
  transition: "transform 0.2s ease-in-out",
  "&:hover": { transform: "scale(1.05) rotate(0deg) !important" },
  zIndex: 12,
});

const CaseFile = styled(DeskItem)({
  width: "300px",
  padding: "20px",
  backgroundColor: "#fdf8e1",
  border: "1px solid #ccc",
  boxShadow: "5px 5px 15px rgba(0,0,0,0.5)",
  transform: "rotate(-5deg)",
  color: "#333",
  bottom: "90px",
  left: "calc(50% - 280px)", // adjust to taste
  // Stack “sheets” behind
  "&::before, &::after": {
    content: '""',
    position: "absolute",
    width: "100%",
    height: "100%",
    backgroundColor: "#fdf8e1",
    border: "1px solid #ccc",
    boxShadow: "3px 3px 10px rgba(0,0,0,0.3)",
  },
  "&::before": {
    top: "-6px",
    left: "-6px",
    transform: "rotate(-4deg)",
    opacity: 0.8,
    zIndex: -1,
  },
  "&::after": {
    top: "-12px",
    left: "-12px",
    transform: "rotate(-3deg)",
    opacity: 0.6,
    zIndex: -2,
  },
});

const CoffeeCup = styled(DeskItem)({
  width: "60px",
  height: "70px",

  position: "absolute", // Ensure positioning context for pseudo-elements
  bottom: "5%",
  left: "calc(50% + 100px)",
  filter: "drop-shadow(8px 6px 8px rgba(0,0,0,0.6))",
  borderRadius: "5px 5px 30px 30px / 5px 5px 20px 20px",

  // ENHANCED: Layered gradients for a 3D, cylindrical look
  background: `
        radial-gradient(circle at 80% 20%, #ffffff 0%, rgba(255,255,255,0) 40%),
        linear-gradient(to right, #d8d8d8, #f5f5f5 30%, #f5f5f5 70%, #d8d8d8)
    `,

  // ENHANCED: This pseudo-element now creates the entire interior (rim + coffee)
  "&::before": {
    content: '""',
    position: "absolute",
    top: "20px",
    left: "4px",
    right: "4px",
    bottom: "3px",
    // The coffee liquid
    background: "radial-gradient(circle at 50% 90%, #3d2a22, #4b3832)",
    borderRadius: "2px 2px 15px 15px / 2px 2px 10px 10px",
    // NEW: This is the KEY to the 3D rim. It creates a strong inner shadow.
    boxShadow: "inset 0px 2px 2px rgba(0,0,0,0.5)",
  },
  // ENHANCED: A sharper, more defined glossy reflection on the side
  "&::after": {
    content: '""',
    position: "absolute",
    top: "10px",
    right: "4px",
    width: "8px",
    height: "30px",
    background: "rgba(255, 255, 255, 0.5)",
    borderRadius: "4px",
    filter: "blur(1px)",
    transform: "rotate(10deg)",
  },
});

// --- UPDATED: Handle with more depth ---
const CoffeeHandle = styled(Box)({
  position: "absolute",
  top: "10px",
  right: "-20px",
  width: "25px",
  height: "40px",
  border: "7px solid #e0e0e0",
  borderLeft: "none",
  borderRadius: "0 15px 15px 0 / 0 10px 10px 0",
  // ENHANCED: Inner shadow and highlight for a more rounded look
  boxShadow:
    "inset -3px 2px 4px rgba(0,0,0,0.2), inset 2px -2px 3px rgba(255,255,255,0.6)",
});

const Steam = styled("div")({
  position: "absolute",
  bottom: "40px",
  left: "50%",
  width: "20px",
  height: "60px",
  transform: "translateX(-50%)",
  background:
    "linear-gradient(to top, rgba(255,255,255,0.6), rgba(255,255,255,0))",
  borderRadius: "50%",
  filter: "blur(2px)",
  animation: `${keyframesSteam} 2.5s ease-in-out infinite`,
  opacity: 1.6,
});

const Ashtray = styled(DeskItem)({
  width: "70px",
  height: "20px",
  backgroundColor: "#444",
  border: "3px solid #333",
  borderRadius: "50%",
  bottom: "120px",
  left: "calc(50% + 180px)",
  transform: "rotate(10deg)",
});

const Cigarette = styled(Box)({
  position: "absolute",
  width: "30px",
  height: "5px",
  backgroundColor: "#fff",
  borderRadius: "2px",
  top: "5px",
  left: "5px",
  "&:before": {
    content: '""',
    position: "absolute",
    right: "0",
    width: "8px",
    height: "5px",
    backgroundColor: "#d2b48c",
  },
  "&:after": {
    content: '""',
    position: "absolute",
    left: "-3px",
    width: "3px",
    height: "5px",
    backgroundColor: "#666",
  },
});

const Smoke = styled("div")({
  position: "absolute",
  top: "-6px",
  left: "-4px",
  width: "12px",
  height: "22px",
  background:
    "linear-gradient(to top, rgba(210,210,210,0.7), rgba(210,210,210,0))",
  borderRadius: "50%",
  filter: "blur(2px)",
  animation: `${keyframesSmoke} 3.6s ease-in-out infinite`,
  opacity: 1,
});

const renderRaindrops = () => {
  const count = 150;
  return Array.from({ length: count }).map((_, i) => {
    const size = Math.random() > 0.7 ? 2 : 1;
    const height = Math.random() > 0.5 ? 30 : 20;
    const duration = Math.random() * 1.5 + 0.5; // Between 0.5s to 2s
    const delay = Math.random() * 5;

    return (
      <Raindrop
        key={i}
        style={{
          left: `${Math.random() * 100}%`,
          width: `${size}px`,
          height: `${height}px`,
          animationDuration: `${Math.random() * 1 + 0.8}s`,
          // CORRECTED: Use a negative delay to start immediately
          animationDelay: `-${Math.random() * 5}s`,
          transform: `rotate(${Math.random() * 10 - 5}deg)`,
        }}
      />
    );
  });
};

// --- The Main 404 Component ---
function Test() {
  const renderElements = (count, Component, props) =>
    Array.from({ length: count }).map((_, i) => (
      <Component key={i} {...props(i)} />
    ));

  return (
    <React.Fragment>
      {globalStyles}
      <PageContainer>
        <WindowContainer>
          <LightningFlash />
          <NeonSignGlow />
          <WindowFrame>
            <WindowMullion className="vertical" />
            <WindowMullion className="horizontal" />
            {renderRaindrops()}
          </WindowFrame>
        </WindowContainer>
        <DeskLampLight>
          {renderElements(50, DustParticle, (i) => ({
            style: {
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDuration: `${Math.random() * 10 + 5}s`,
              animationDelay: `${Math.random() * 15}s`,
            },
          }))}
        </DeskLampLight>

        <Tooltip
          title="You've stumbled upon a clue. Keep searching...."
          placement="top"
          arrow
        >
          <CaseFile>
            <Typography
              variant="h5"
              component="h1"
              sx={{ fontFamily: "inherit", fontWeight: "bold" }}
            >
              Case #404
            </Typography>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontFamily: "inherit",
                my: 2,
                color: "#b71c1c",
                fontWeight: "bold",
              }}
            >
              The Missing Page
            </Typography>
            <Typography variant="body1" sx={{ fontFamily: "inherit" }}>
              The billboard is not available here. Enjoy the rain or...{" "}
            </Typography>
            <Button
              onClick={() => (window.location.href = "/")}
              sx={{
                fontFamily: "inherit",
                mt: 2,
                color: "#333",
                borderColor: "#333",
              }}
            >
              Return to Headquarters
            </Button>
          </CaseFile>
        </Tooltip>

        <Tooltip title="The puzzle solves in the smoke.." placement="top" arrow>
          <CoffeeCup>
            <CoffeeHandle />
            <Steam style={{ animationDelay: "0s" }} />
            <Steam style={{ animationDelay: "1.2s" }} />
          </CoffeeCup>
        </Tooltip>

        <Tooltip title="Crafted by Abhi....." placement="top" arrow>
          <Ashtray>
            <Cigarette>
              <Smoke style={{ animationDelay: "0s" }} />
              <Smoke style={{ animationDelay: "2s" }} />
            </Cigarette>
          </Ashtray>
        </Tooltip>

        <Desk />
      </PageContainer>
    </React.Fragment>
  );
}

export default Test;
